import {
  CheckCircle,
  Info,
  Sparkle,
  Warning,
  X,
  XCircle,
} from '@phosphor-icons/react/ssr';
import type { ComponentProps, ElementType, ReactNode } from 'react';

import { tv } from '../../lib/tv';
import { Button } from './button';

export type BannerTone = 'info' | 'success' | 'warning' | 'danger' | 'brand';
export type BannerVariant = 'subtle' | 'solid';

/**
 * The Figma Banner (85:5 / 89:10640): full width, 48 tall; padding 12 / 12 /
 * 12 / 16 (Brand Solid 8 top and bottom, for its 32px Button), gap space/3.
 * 20px icon · Text (Title Label/Medium + 8 + Description Body/Medium, on a
 * shared baseline) · Action (Label/Small with a 1px underline, 1px below) ·
 * 16px Dismiss ×. Subtle: status bg + a 1px bottom border (inside the 48);
 * Solid: the status solid fill, inverse text (Warning: ink), description at
 * 85% opacity (100% on Info / Success / Danger, where 85% fails contrast).
 */
const banner = tv({
  slots: {
    root: 'flex min-h-12 w-full items-center gap-3 py-3 pr-3 pl-4',
    icon: 'size-5 shrink-0',
    text: 'flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2',
    title: 'text-label-md',
    description: 'text-body-md',
    action: [
      'text-label-sm flex shrink-0 flex-col gap-px rounded-xs outline-none',
      'focus-visible:ring-2 focus-visible:ring-border-focus',
    ],
    underline: 'h-px w-full bg-current',
    dismiss: [
      // 16px glyph, 32px hit area (Figma note).
      'relative flex size-4 shrink-0 items-center justify-center rounded-xs outline-none',
      'after:absolute after:-inset-2 after:content-[""]',
      'focus-visible:ring-2 focus-visible:ring-border-focus',
    ],
  },
  variants: {
    variant: {
      subtle: {
        root: 'border-b',
        title: 'text-text-primary',
        description: 'text-text-secondary',
        action: 'text-text-link',
        dismiss: 'text-icon-secondary',
      },
      solid: {
        title: 'text-text-inverse',
        description: 'text-text-inverse opacity-85',
        action: 'text-text-inverse',
        dismiss: 'text-icon-inverse',
      },
    },
    tone: { info: {}, success: {}, warning: {}, danger: {}, brand: {} },
  },
  compoundVariants: [
    {
      variant: 'subtle',
      tone: 'info',
      class: {
        root: 'bg-status-info-bg border-status-info-border',
        icon: 'text-status-info-fg',
      },
    },
    {
      variant: 'subtle',
      tone: 'success',
      class: {
        root: 'bg-status-success-bg border-status-success-border',
        icon: 'text-status-success-fg',
      },
    },
    {
      variant: 'subtle',
      tone: 'warning',
      class: {
        root: 'bg-status-warning-bg border-status-warning-border',
        icon: 'text-status-warning-fg',
      },
    },
    {
      variant: 'subtle',
      tone: 'danger',
      class: {
        root: 'bg-status-danger-bg border-status-danger-border',
        icon: 'text-status-danger-fg',
      },
    },
    {
      variant: 'subtle',
      tone: 'brand',
      class: {
        root: 'bg-brand-subtle border-brand-subtle-hover',
        icon: 'text-icon-brand',
        // Figma text/link is 4.18:1 on brand/subtle (axe fails 12px text):
        // the nearest passing token, text/brand (olive/900).
        action: 'text-text-brand',
      },
    },
    {
      variant: 'solid',
      tone: 'info',
      // Figma's 85% description is ≈4:1 here (axe fails): full text/inverse.
      class: {
        root: 'bg-status-info-solid',
        icon: 'text-text-inverse',
        description: 'opacity-100',
      },
    },
    {
      variant: 'solid',
      tone: 'success',
      // Figma's 85% description is ≈4:1 here (axe fails): full text/inverse.
      class: {
        root: 'bg-status-success-solid',
        icon: 'text-text-inverse',
        description: 'opacity-100',
      },
    },
    {
      // Warning Solid uses ink text (7.1:1 on amber).
      variant: 'solid',
      tone: 'warning',
      class: {
        root: 'bg-status-warning-solid',
        icon: 'text-text-primary',
        title: 'text-text-primary',
        description: 'text-text-primary',
        action: 'text-text-primary',
        dismiss: 'text-icon-primary',
      },
    },
    {
      variant: 'solid',
      tone: 'danger',
      // Figma's 85% description is ≈4:1 here (axe fails): full text/inverse.
      class: {
        root: 'bg-status-danger-solid',
        icon: 'text-text-inverse',
        description: 'opacity-100',
      },
    },
    {
      variant: 'solid',
      tone: 'brand',
      class: { root: 'bg-bg-inverse py-2', icon: 'text-text-link-inverse' },
    },
  ],
  defaultVariants: { variant: 'subtle', tone: 'info' },
});

/** Figma icon per Tone (Phosphor Regular). */
const TONE_ICON: Record<BannerTone, ElementType<{ className?: string }>> = {
  info: Info,
  success: CheckCircle,
  warning: Warning,
  danger: XCircle,
  brand: Sparkle,
};

export type BannerAction = {
  /** Verb phrase: "Update billing", "View status". */
  label: string;
  /** Renders a link. */
  href?: string;
  /** Renders a button (when there is no `href`). */
  onClick?: () => void;
};

export type BannerProps = Omit<ComponentProps<'div'>, 'title' | 'children'> & {
  /** Figma "Tone". Highest severity wins when two banners compete - show one at a time. */
  tone?: BannerTone;
  /** Figma "Style": Subtle for most messages; Solid only for account-blocking or time-critical ones. */
  variant?: BannerVariant;
  /** Figma "Title": the message in a few words. */
  title: ReactNode;
  /** Figma "Description" (optional): one sentence on the same line. */
  description?: ReactNode;
  /** Defaults to the tone's icon; `false` hides it. */
  icon?: ElementType<{ className?: string }> | false;
  /**
   * Figma "Action": a text link - or, on Brand Solid (trial / upgrade), a
   * Small Primary Button for the conversion action.
   */
  action?: BannerAction;
  /** Figma "Dismiss": shows the × (a 32px hit area). Move focus somewhere sensible after hiding it. */
  onDismiss?: () => void;
  /** Accessible name of the ×. */
  dismissLabel?: string;
};

/**
 * Page- or app-level message that persists until resolved or dismissed:
 * billing, trials, maintenance, storage (Figma: Components ↳ Banner). Full
 * width at the top of the content area. A live region: `role="alert"` for
 * Danger (announced at once), `role="status"` otherwise (polite) - pass
 * `role` to override. Use a Well for inline, section-level notes and a Toast
 * for transient feedback.
 */
export function Banner({
  tone = 'info',
  variant = 'subtle',
  title,
  description,
  icon,
  action,
  onDismiss,
  dismissLabel = 'Dismiss',
  className,
  ...props
}: BannerProps) {
  const styles = banner({ tone, variant });
  const Icon = icon === false ? null : (icon ?? TONE_ICON[tone]);
  const buttonAction = variant === 'solid' && tone === 'brand';
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      data-slot="banner"
      data-tone={tone}
      data-variant={variant}
      className={styles.root({ className })}
      {...props}
    >
      {Icon && <Icon aria-hidden data-slot="banner-icon" className={styles.icon()} />}
      <div data-slot="banner-text" className={styles.text()}>
        <span data-slot="banner-title" className={styles.title()}>
          {title}
        </span>
        {description && (
          <span data-slot="banner-description" className={styles.description()}>
            {description}
          </span>
        )}
      </div>
      {action &&
        (buttonAction ? (
          action.href ? (
            <Button asChild size="sm" data-slot="banner-action">
              <a href={action.href}>{action.label}</a>
            </Button>
          ) : (
            <Button size="sm" data-slot="banner-action" onClick={action.onClick}>
              {action.label}
            </Button>
          )
        ) : action.href ? (
          <a href={action.href} data-slot="banner-action" className={styles.action()}>
            {action.label}
            <span aria-hidden className={styles.underline()} />
          </a>
        ) : (
          <button
            type="button"
            data-slot="banner-action"
            className={styles.action()}
            onClick={action.onClick}
          >
            {action.label}
            <span aria-hidden className={styles.underline()} />
          </button>
        ))}
      {onDismiss && (
        <button
          type="button"
          aria-label={dismissLabel}
          data-slot="banner-dismiss"
          className={styles.dismiss()}
          onClick={onDismiss}
        >
          <X aria-hidden className="size-4" />
        </button>
      )}
    </div>
  );
}
